import * as XLSX from "xlsx";

const toPerMasterCaseValue = (value, masterCaseWeightKg) => {

  const hasValue =
    value !== null && value !== undefined && String(value).trim() !== "";
  if (!hasValue) return "";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "";

  const divisor = Number(masterCaseWeightKg);
  if (divisor > 0) {
    return Number((numericValue / divisor).toFixed(6));
  }
  return numericValue;
};
const resolveUnit2 = (uom) => {
  const normalized = String(uom || "").toLowerCase().trim();
  return normalized === "kg" ? "lbs" : "oz";
};

const valueFrom = (source, paths = []) => {
  for (const path of paths) {
    const value = path.split(".").reduce((acc, key) => acc?.[key], source);
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

export const exportToExcel = (data, filename) => {
  if (!data || data.length === 0) return;
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const numberFrom = (source, paths = []) => {
  const value = valueFrom(source, paths);
  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const INGREDIENT_IMPORT_HEADERS = [
  "SKU",
  "Description",
  "EAN/UPC",
  "UOM",
  "Selling price",
  "Category",
  "Trademark",
  "Tax category",
  "Supplier",
  "SKU (Supplier)",
  "Purchase price",
  "Landing cost",
  "FDA Code",
  "FR",
  "Ingredients",
  "IngrÃ©dients",
  "Allergens",
  "AllergÃ¨nes",
  "Name EN",
  "Name FR",
  "Raw Material SKU",
  "Qty/Un",
  "Un",
  "Net Weight 1",
  "Unit 1",
  "Net Weight 2",
  "Unit 2",
  "CatÃ©gorie",
  "MSRP (CAD)",
  "MSRP (USD)",
  "L ('')",
  "W ('')",
  "H ('')",
  "L (mm)",
  "W (mm)",
  "H (mm)",
  "Ti",
  "Hi",
  "New",
  "Label Category",
  "Country of Origin",
  "Harmonized Tariff",
  "Shipping Days",
  "Minimum Order Quantity",
  "Order Step Quantity",
  "Expiry Date",
];

const RAW_MATERIAL_IMPORT_HEADERS = [
  "SKU",
  "Description",
  "EAN/UPC",
  "UOM",
  "Selling price",
  "Category",
  "Trademark",
  "Storage location",
  "Tax category",
  "Beginning inventory",
  "Stock price",
  "Supplier",
  "Purchase price",
  "Landing cost",
  "SKU (Supplier)", 
  "Harmonized Tariff",
  "Duty Rate (%)",
  "Minimum order quantity",
  "Order step quantity",
  "FDA Code",
  "FR",
  "Ingredients",
  "Ingrédients",
  "Allergens",
  "Allergènes",
  "Name EN",
  "Name FR",
  "Raw Material SKU",
  "Qty/Un",
  "Un",
  "Net Weight 1",
  "Unit 1",
  "Net Weight 2",
  "Unit 2",
  "Catégorie",
  "MSRP (CAD)",
  "MSRP (USD)",
  "L ('')",
  "W ('')",
  "H ('')",
  "L (mm)",
  "W (mm)",
  "H (mm)",
  "Ti",
  "Hi",
  "New",
  "Label Category",
  "Country of Origin",
  "Expiry Date",
];

const getSecondUomHeaders = (maxUomCount = 2) => {
  const headers = ["SKU"];
  for (let i = 0; i < maxUomCount; i++) {
    headers.push(
      "EAN/UPC",
      "Second UOM",
      "Quantity Default",
      "Description",
      "Type",
      "Label Printing"
    );
  }
  return headers;
};

const getSecondUomColWidths = (maxUomCount = 2) => {
  const colWidths = [{ wch: 18 }];
  for (let i = 0; i < maxUomCount; i++) {
    colWidths.push(
      { wch: 18 },
      { wch: 14 },
      { wch: 18 },
      { wch: 45 },
      { wch: 10 },
      { wch: 16 }
    );
  }
  return colWidths;
};

export const buildFrDescription = (p = {}, row = null) => {
  const isIngredient =
    String(p.type || "")
      .trim()
      .toLowerCase() === "ingredient";

  const nameFr = String(
    p.nameFrCustom ||
    p.nameFr ||
    row?.nameFr ||
    p.customFields?.nameFr ||
    ""
  ).trim();

  const nameEn = String(
    p.nameEnCustom ||
    p.nameEn ||
    row?.nameEn ||
    p.customFields?.nameEn ||
    ""
  ).trim();

  const baseName = nameFr || nameEn;
  const trademark = String(p.trademark || "").trim();

  const descEn = String(
    row?.descriptionEn ||
    p.descriptionEn ||
    ""
  ).trim();

  if (descEn && nameEn && baseName) {
    // Escape regex special chars
    const escapedNameEn = nameEn.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const nameEnRegex = new RegExp(`^${escapedNameEn}`, "i");
    if (nameEnRegex.test(descEn)) {
      return descEn.replace(nameEnRegex, baseName).trim();
    }
  }

  if (!baseName) return p.fr || p.descriptionFr || "";

  const hasTrademark = trademark && baseName.toUpperCase().includes(trademark.toUpperCase());
  const includeTrademark = !isIngredient && trademark && !hasTrademark;
  const parts = [baseName, includeTrademark ? trademark : "", "VRAC"].filter(Boolean);
  return parts.join(" ").replace(/\s+/g, " ").trim();
};

export const exportRecipeToExcel = ({
  product,
  ingredients,
  totalWeight,
  ingredientCostTotal,
  packagingCostNum,
  totalBatchCost,
  acctCostPerKg,
  addedPackaging,
  masterCase,
  variantRows = [],
  masterCaseWeightKg = 25,
  barcodeSecondUomRows = [],
  wholesale3Cad = 0,
  additionalExportColumns = {},
}) => {
  const resolvedTaxCategory =
    product?.taxCategory ||
    product?.tax_category ||
    product?.taxcategory ||
    product?.customFields?.taxCategory ||
    "Tax exemption";

  const hasVariantRows = Array.isArray(variantRows) && variantRows.length > 0;
  const isBaseProductExport = !product?.isVariant && !hasVariantRows;

  const isIngredientType =
    String(product?.type || "")
      .trim()
      .toLowerCase() === "ingredient" &&
    !hasVariantRows;

  const fulfillmentValue = isIngredientType
    ? product?.supplier || ""
    : "Nutrivilla Foods";

  const isFilledIngredient = (ing) =>
    String(ing?.name || "").trim() || String(ing?.sku || "").trim();

  const primaryIngredients = Array.isArray(ingredients) ? ingredients : [];
  const productIngredients = Array.isArray(product?.ingredients)
    ? product.ingredients
    : [];
  const bomIngredients = primaryIngredients.some(isFilledIngredient)
    ? primaryIngredients
    : productIngredients;
    

  const getAdjustedWeight = (ing) => {
    const ratio = Number(ing.ratio) || 0;
    if (ratio <= 0 || masterCaseWeightKg <= 0) {
      return Number(ing.weight) || 0;
    }
    return Number(((ratio / 100) * masterCaseWeightKg).toFixed(4));
  };

  const buildBomSheetRows = () => {
    const rows = [["SKU", "SKU", "QUANTITY"]];

    if (hasVariantRows) {
      const sortedVariantRows = [...variantRows].sort((a, b) => {
        const skuA = String(a?.sku || "").trim();
        const skuB = String(b?.sku || "").trim();
        return skuA.localeCompare(skuB, undefined, { numeric: true, sensitivity: 'base' });
      });

      const variantRowsForBom = sortedVariantRows.flatMap((row) => {
        const newSku = String(row?.sku || "").trim();
        if (!newSku) return [];
        const baseSku = String(row?.baseSku || "").trim();
        const variantWeightKg =
          Number(row?.netWeightKg) || Number(row?.actualWeight) || 0;

        const packagingItems = Array.isArray(row?.packaging)
          ? row.packaging.flatMap((pack) =>
            Array.isArray(pack?.items) ? pack.items : [],
          )
          : [];

        const baseRow =
          baseSku && variantWeightKg > 0
            ? [[newSku, baseSku, variantWeightKg]]
            : [];

        const packagingRows = packagingItems
          .filter((item) => String(item?.sku || "").trim())
          .map((item) => [
            newSku,
            String(item.sku || "").trim(),
            Number(item.qty) || 0,
          ]);

        return baseRow.concat(packagingRows);
      });

      return rows.concat(variantRowsForBom);
    }

    const baseSku = String(
      product?.sku || product?.baseProductSku || "",
    ).trim();

    const ingredientRows = (bomIngredients || [])
      .filter((ing) => String(ing?.sku || "").trim())
      .map((ing) => [
        baseSku,
        String(ing.sku || "").trim(),
        getAdjustedWeight(ing),
      ]);

    const packagingRows = (addedPackaging?.items || [])
      .filter((item) => String(item?.sku || "").trim())
      .map((item) => [
        baseSku,
        String(item.sku || "").trim(),
        Number(item.qty) || 0,
      ]);

    if (ingredientRows.length === 0 && packagingRows.length === 0) {
      return rows;
    }

    return rows.concat(ingredientRows, packagingRows);
  };

  const buildSecondUomSheetRows = () => {
    const baseSku = String(product?.sku || product?.baseProductSku || "").trim();
    const filteredRows = Array.isArray(barcodeSecondUomRows)
      ? barcodeSecondUomRows.filter((row) => String(row?.eanUpc || "").trim())
      : [];

    const sortedRows = [...filteredRows].sort((a, b) => {
      const qA = Number(a?.quantityDefault) || 0;
      const qB = Number(b?.quantityDefault) || 0;
      return qA - qB;
    });

    const maxUomCount = Math.max(2, sortedRows.length);
    const headers = getSecondUomHeaders(maxUomCount);

    if (sortedRows.length === 0) {
      return [headers, [baseSku, "", "", "", "", "", "", "", "", "", "", "", ""]];
    }

    const row = [baseSku];
    for (let i = 0; i < maxUomCount; i++) {
      const uomRow = sortedRows[i];
      if (uomRow) {
        row.push(
          String(uomRow.eanUpc || "").trim(),
          String(uomRow.secondUom || "").trim(),
          Number(uomRow.quantityDefault) || 0,
          String(uomRow.description || "").trim(),
          String(uomRow.type || "").trim(),
          String(uomRow.labelPrinting || "").trim()
        );
      } else {
        row.push("", "", "", "", "", "");
      }
    }

    return [headers, row];
  };

  const ERPAG_HEADERS = [
    "SKU",
    "Description",
    "EAN/UPC",
    "UOM",
    "Selling price",
    "Category",
    "Trademark",
    "Tax category",
    "Supplier",
    "Base Quantity",
    "Purchase price",
    "Landed cost",
    "SKU (Supplier)",
    "FDA Code",
    "FR",
    "Ingredients",
    "Ingrédients",
    "Allergens",
    "Allergènes",
    "Name EN",
    "Name FR",
    "Raw Material SKU",
    "Qty/Un",
    "Un",
    "Net Weight 1",
    "Unit 1",
    // "Net Weight (oz/lbs)",
    "Net Weight 2",
    "Unit 2",
    "Catégorie",
    "MSRP (CAD)",
    "MSRP (USD)",
    "L ('')",
    "W ('')",
    "H ('')",
    "L (mm)",
    "W (mm)",
    "H (mm)",
    "Ti",
    "Hi",
    "New",
    "Label Category",
    "Country of Origin",
    "Harmonized Tariff",
    ...(isIngredientType ? ["Shipping Days", "Minimum Order Quantity", "Order Step Quantity"] : []),
    "Expiry Date",
  ];


  const resolveExpiryDays = (value) => {
    const trimmed = String(value || "").trim();
    return trimmed ? trimmed : "1095";
  };

  let exportHeaders = isIngredientType
    ? [...RAW_MATERIAL_IMPORT_HEADERS]
    : [...ERPAG_HEADERS];

  if (!isIngredientType) {
    exportHeaders = exportHeaders.filter((h) => h !== "Purchase price");
  }

  const additionalColKeys = Object.keys(additionalExportColumns);
  if (additionalColKeys.length > 0) {
    exportHeaders = exportHeaders.concat(additionalColKeys);
  }

  const buildErpagRowForVariant = (row) => {
    const variantFields = row?.customFields || {};
    const baseFields = row?.baseCustomFields || variantFields || {};
    const nameEn = String(baseFields.nameEn || row?.nameEn || "");
    const nameFr = String(baseFields.nameFr || row?.nameFr || "");
    const lineCost = Number(row?.lineCost || 0);
    const bomCost = Number(row?.bomCost || 0);
    const packagingCost =
      Number(row?.packagingCost || 0) ||
      (Array.isArray(row?.packaging)
        ? row.packaging.reduce((sum, pack) => {
          const items = Array.isArray(pack?.items) ? pack.items : [];
          const packCost = items.reduce(
            (subSum, item) =>
              subSum +
              (Number(item?.qty) || 0) * (Number(item?.unitCost) || 0),
            0,
          );
          return sum + packCost;
        }, 0)
        : 0);
    const packagingSizeWeightKg = (() => {
      const firstPack = Array.isArray(row?.packaging) ? row.packaging[0] : null;
      const sizeText = String(firstPack?.size || "").trim().toLowerCase();
      const match = sizeText.match(/(\d+(\.\d+)?)\s*kg/);
      return match ? Number(match[1]) : 0;
    })();
    const variantWeightKg =
      Number(row?.netWeightKg) ||
      Number(row?.actualWeight) ||
      packagingSizeWeightKg ||
      0;
    const isMasterCase = Boolean(row?.isMasterCase);
    const frValue = buildFrDescription(product, row);
    const mfgCostPerKg = Number(row?.mfgCostPerKg || 0);
    const materialCostPerKg = mfgCostPerKg > 0 ? mfgCostPerKg : bomCost;
    const estimatedTotal =
      variantWeightKg > 0 && materialCostPerKg > 0
        ? materialCostPerKg * variantWeightKg + packagingCost
        : bomCost > 0
          ? bomCost + packagingCost
          : lineCost > 0
            ? lineCost
            : 0;
    const resolvedEstimatedCost =
      estimatedTotal > 0 ? estimatedTotal.toFixed(6) : "";
    const countryOfOrigin =
      row?.countryOfOrigin ||
      variantFields.countryOfOrigin ||
      baseFields.countryOfOrigin ||
      "Canada";
    const baseNetWeightKg = Number(baseFields.netWeightGKg || 0);
    const baseNetWeightLbs = Number(baseFields.netWeightOzLbs || 0);
    const baseQtyUn = Number(baseFields.qtyUn || 0);
    const variantNetWeightKg =
      Number(row?.netWeightKg) || Number(row?.actualWeight) || 0;
    const variantMasterCaseWeightKg = variantNetWeightKg > 0
      ? variantNetWeightKg
      : Number(masterCaseWeightKg) || 0;
    const weightMultiplier =
      baseNetWeightKg > 0
        ? variantNetWeightKg / baseNetWeightKg
        : variantNetWeightKg > 0
          ? variantNetWeightKg
          : 1;
    const resolvedQtyUn =
      baseQtyUn > 0 ? Number((baseQtyUn * weightMultiplier).toFixed(4)) : "";
    const resolvedNetWeightKg =
      baseNetWeightKg > 0
        ? Number((baseNetWeightKg * weightMultiplier).toFixed(4))
        : baseFields.netWeightGKg || "";
    const resolvedNetWeightLbs =
      baseNetWeightLbs > 0
        ? Number((baseNetWeightLbs * weightMultiplier).toFixed(4))
        : baseFields.netWeightOzLbs || "";
    const resolveVariantFrenchCategory = () => {
      const englishCategory = String(row?.category || "").trim();
      const baseFrenchCategory = String(baseFields?.categorie || "").trim();
      const normalized = englishCategory.toLowerCase();

      const weightFromCategory = (() => {
        const match = englishCategory.match(/(\d+(\.\d+)?)\s*kg/i);
        return match ? Number(match[1]) : 0;
      })();
      const resolvedWeight =
        weightFromCategory > 0 ? weightFromCategory : Number(variantNetWeightKg);

      if (normalized.startsWith("spices bulk")) {
        if (isMasterCase || normalized.includes("master")) {
          return "Épices VRAC MASTER";
        }
        if (resolvedWeight > 0) {
          return `Épices VRAC ${resolvedWeight}KG`;
        }
        return "Épices en VRAC";
      }

      if (normalized.startsWith("spices custom")) {
        if (isMasterCase || normalized.includes("master")) {
          return "Épices CLIENT MASTER";
        }
        if (resolvedWeight > 0) {
          return `Épices CLIENT ${resolvedWeight}KG`;
        }
        return "Épices personnalisées VRAC";
      }

      return baseFrenchCategory || englishCategory;
    };

    return [
      row?.sku || "",
      row?.descriptionEn || "",
      row?.upc || "",
      "CS",
      toPerMasterCaseValue(row?.sellingPrice, variantMasterCaseWeightKg),
      row?.category || "",
      product?.trademark || "",
      resolvedTaxCategory,
      fulfillmentValue,
      variantMasterCaseWeightKg || "",
      "", // Landed cost
      product?.supplier || "", // SKU (Supplier)
      baseFields.fdaCode || row?.fdaCode || "",
      frValue,
      baseFields.ingredientsEn || "",
      baseFields.ingredientsFr || "",
      baseFields.allergensEn || "",
      baseFields.allergensFr || "",
      nameEn,
      nameFr,
      baseFields.rawMaterialSku || row?.baseSku || "",
      resolvedQtyUn,
      "CS",
      resolvedNetWeightKg,
      baseFields.gKg || "",
      resolvedNetWeightLbs,
      resolveUnit2(product?.uom),
      resolveVariantFrenchCategory(),
      toPerMasterCaseValue(
        Number(row?.baseMsrpCad) || product?.msrpCad || "",
        variantMasterCaseWeightKg,
      ),
      toPerMasterCaseValue(
        Number(row?.baseMsrpUsd) || product?.msrpUsd || "",
        variantMasterCaseWeightKg,
      ),
      row?.lengthInch || "",
      row?.widthInch || "",
      row?.heightInch || "",
      row?.lengthMm || "",
      row?.widthMm || "",
      row?.heightMm || "",
      row?.ti || "",
      row?.hi || "",
      "NEW",
      "",
      countryOfOrigin,
      row?.harmonizedTariff ||
      variantFields.harmonizedTariff ||
      baseFields.harmonizedTariff ||
      "",
      resolveExpiryDays(
        row?.expiryDays || variantFields.expiryDays || baseFields.expiryDays,
      ),
    ];
  };

  if (hasVariantRows || isBaseProductExport) {
    const safeName = (product.nameEn || "Recipe").replace(/[^a-zA-Z0-9]/g, "_");
    const dateStr = new Date().toISOString().slice(0, 10);
    const baseFileName = `${safeName}_Export_${dateStr}`;

    const wbProduct = XLSX.utils.book_new();
    if (hasVariantRows) {
      const sortedVariantRows = [...variantRows].sort((a, b) => {
        const skuA = String(a?.sku || "").trim();
        const skuB = String(b?.sku || "").trim();
        return skuA.localeCompare(skuB, undefined, { numeric: true, sensitivity: 'base' });
      });

      const variantSheetRows = [
        exportHeaders,
        ...sortedVariantRows.map((row) => buildErpagRowForVariant(row)),
      ];
      const ws = XLSX.utils.aoa_to_sheet(variantSheetRows);
      ws["!cols"] = exportHeaders.map(() => ({ wch: 20 }));
      XLSX.utils.book_append_sheet(wbProduct, ws, "Product Data");
    } else if (isBaseProductExport) {
      const sellingPrice = numberFrom(product, ["defaultPrice", "sellingPrice"]);
      const purchasePrice = numberFrom(product, [
        "purchasePrice",
        "suppliers.purchasePrice",
      ]);
      const landingCost = numberFrom(product, [
        "landingCost",
        "suppliers.landingCost",
      ]);
      const estimatedCost = isIngredientType
        ? purchasePrice
        : Number(acctCostPerKg || 0);
      const resolvedMsrpCad = isIngredientType
        ? product.msrpCad === "" || product.msrpCad === null || product.msrpCad === undefined
          ? 0
          : product.msrpCad
        : product.msrpCad || "";
      const resolvedMsrpUsd = isIngredientType
        ? product.msrpUsd === "" || product.msrpUsd === null || product.msrpUsd === undefined
          ? 0
          : product.msrpUsd
        : product.msrpUsd || "";
      const productRow = [
        product.sku || "",
        product.descriptionEn || "",
        product.upc || "",
        product.uom || "kg",
        // toPerMasterCaseValue(product.defaultPrice || "", masterCaseWeightKg),
        wholesale3Cad > 0 && masterCaseWeightKg > 0
          ? Number(wholesale3Cad / masterCaseWeightKg).toFixed(2)
          : (product.defaultPrice || ""),
        product.itemCategory || "",
        product.trademark || "",
        resolvedTaxCategory,
        fulfillmentValue,
        masterCaseWeightKg || "",
        // estimatedCost ? estimatedCost.toFixed(4) : "",
        landingCost > 0 ? landingCost.toFixed(4) : "",
        isIngredientType
          ? product.supplierSku || ""
          : product.supplier || "",
        product.fdaCode || "",
        buildFrDescription(product),
        product.ingredientsEn || "",
        product.ingredientsFr || "",
        product.allergensEn || "",
        product.allergensFr || "",
        product.nameEnCustom || product.nameEn || "",
        product.nameFrCustom || product.nameFr || "",
        product.rawMaterialSku || product.sku || "",
        product.qtySku || "",
        product.qtySkuUnit || product.uom || "",
        product.netWeightGkKg || "",
        product.gkKgUnit || "",
        product.netWeightOzLbs || "",
        resolveUnit2(product.uom),
        product.categorie || "",
        toPerMasterCaseValue(resolvedMsrpCad, masterCaseWeightKg),
        toPerMasterCaseValue(resolvedMsrpUsd, masterCaseWeightKg),
        product.lengthInch || "",
        product.widthInch || "",
        product.heightInch || "",
        product.lengthMm || "",
        product.widthMm || "",
        product.heightMm || "",
        addedPackaging?.ti || product.ti || "",
        addedPackaging?.hi || product.hi || "",
        product.new || "New",
        "",
        product.countryOfOrigin || "",
        product.harmonizedTariff || "",
        ...(isIngredientType ? [
          product.shippingDays || "",
          product.minimumOrderQuantity || "",
          product.orderStepQuantity || "",
        ] : []),
        resolveExpiryDays(product.expiryDays),
        ...additionalColKeys.map(k => additionalExportColumns[k] || "")
      ];
      const ingredientRow = [
        product.sku || "",
        product.descriptionEn || product.description || "",
        product.upc || product.eanUpc || "",
        product.uom || "kg",
        wholesale3Cad > 0
          ? Number(wholesale3Cad).toFixed(2)
          : sellingPrice > 0
          ? sellingPrice
          : "",
        product.itemCategory || product.category || "",
        product.trademark || "",
        resolvedTaxCategory,
        valueFrom(product, ["supplier", "suppliers.supplier"]),
        "",
        purchasePrice > 0 ? purchasePrice.toFixed(4) : "",
        landingCost > 0 ? landingCost.toFixed(4) : "",
        product.supplierSku || "",
        product.fdaCode || valueFrom(product, ["customFields.fdaCode"]),
        product.fr || product.descriptionFr || valueFrom(product, ["customFields.fr"]),
        product.ingredientsEn || valueFrom(product, ["customFields.ingredients"]),
        product.ingredientsFr || product.customFields?.["ingrÃ©dients"] || "",
        product.allergensEn || valueFrom(product, ["customFields.allergens"]),
        product.allergensFr || product.customFields?.["allergÃ¨nes"] || "",
        product.nameEnCustom || product.nameEn || valueFrom(product, ["customFields.nameEn"]),
        product.nameFrCustom || product.nameFr || valueFrom(product, ["customFields.nameFr"]),
        product.rawMaterialSku || valueFrom(product, ["customFields.rawMaterialSku"]) || product.sku || "",
        product.qtySku || valueFrom(product, ["customFields.qtyUn"]),
        product.qtySkuUnit || valueFrom(product, ["customFields.un"]) || product.uom || "",
        product.netWeightGkKg || valueFrom(product, ["customFields.netWeightGKg"]),
        product.gkKgUnit || valueFrom(product, ["customFields.gKg"]),
        product.netWeightOzLbs || valueFrom(product, ["customFields.netWeightOzLbs"]),
        resolveUnit2(product.uom),
        product.categorie || product.customFields?.["catÃ©gorie"] || "",
        resolvedMsrpCad,
        resolvedMsrpUsd,
        product.lengthInch || "",
        product.widthInch || "",
        product.heightInch || "",
        product.lengthMm || "",
        product.widthMm || "",
        product.heightMm || "",
        addedPackaging?.ti || product.ti || "",
        addedPackaging?.hi || product.hi || "",
        product.new || valueFrom(product, ["customFields.new"]) || "New",
        product.labelCategory || valueFrom(product, ["customFields.labelCategory"]),
        product.countryOfOrigin || "",
        product.harmonizedTariff || "",
        valueFrom(product, ["shippingDays", "suppliers.shippingDays"]),
        valueFrom(product, [
          "minimumOrderQuantity",
          "suppliers.minimumOrderQuantity",
        ]),
        valueFrom(product, ["orderStepQuantity", "suppliers.orderStepQuantity"]),
        resolveExpiryDays(product.expiryDays || valueFrom(product, ["customFields.expiryDays"])),
        ...additionalColKeys.map(k => additionalExportColumns[k] || "")
      ];
      const rawMaterialImportRow = [
        product.sku || "",
        product.descriptionEn || product.description || "",
        product.upc || product.eanUpc || "",
        product.uom || "kg",
        wholesale3Cad > 0 && masterCaseWeightKg > 0
          ? Number(wholesale3Cad / masterCaseWeightKg).toFixed(2)
          : sellingPrice > 0
          ? sellingPrice
          : "",
        product.itemCategory || product.category || "",
        product.trademark || "",
        "",
        resolvedTaxCategory,
        "",
        "",
        valueFrom(product, ["supplier", "suppliers.supplier"]),
        purchasePrice > 0 ? purchasePrice.toFixed(4) : "",
        landingCost > 0 ? landingCost.toFixed(4) : "",
        product.supplierSku || "",
        product.harmonizedTariff || "",
        "",
        valueFrom(product, [
          "minimumOrderQuantity",
          "suppliers.minimumOrderQuantity",
        ]),
        valueFrom(product, ["orderStepQuantity", "suppliers.orderStepQuantity"]),
        product.fdaCode || valueFrom(product, ["customFields.fdaCode"]),
        product.fr || product.descriptionFr || valueFrom(product, ["customFields.fr"]),
        product.ingredientsEn || valueFrom(product, ["customFields.ingredients"]),
        product.ingredientsFr || product.customFields?.["ingrÃ©dients"] || "",
        product.allergensEn || valueFrom(product, ["customFields.allergens"]),
        product.allergensFr || product.customFields?.["allergÃ¨nes"] || "",
        product.nameEnCustom || product.nameEn || valueFrom(product, ["customFields.nameEn"]),
        product.nameFrCustom || product.nameFr || valueFrom(product, ["customFields.nameFr"]),
        product.rawMaterialSku || valueFrom(product, ["customFields.rawMaterialSku"]) || product.sku || "",
        product.qtySku || valueFrom(product, ["customFields.qtyUn"]),
        product.qtySkuUnit || valueFrom(product, ["customFields.un"]) || product.uom || "",
        product.netWeightGkKg || valueFrom(product, ["customFields.netWeightGKg"]),
        product.gkKgUnit || valueFrom(product, ["customFields.gKg"]),
        product.netWeightOzLbs || valueFrom(product, ["customFields.netWeightOzLbs"]),
        resolveUnit2(product.uom),
        product.categorie || product.customFields?.["catÃ©gorie"] || "",
        resolvedMsrpCad,
        resolvedMsrpUsd,
        product.lengthInch || "",
        product.widthInch || "",
        product.heightInch || "",
        product.lengthMm || "",
        product.widthMm || "",
        product.heightMm || "",
        addedPackaging?.ti || product.ti || "",
        addedPackaging?.hi || product.hi || "",
        product.new || valueFrom(product, ["customFields.new"]) || "New",
        product.labelCategory || valueFrom(product, ["customFields.labelCategory"]),
        product.countryOfOrigin || "",
        resolveExpiryDays(product.expiryDays || valueFrom(product, ["customFields.expiryDays"])),
        ...additionalColKeys.map(k => additionalExportColumns[k] || "")
      ];
      const row = isIngredientType ? rawMaterialImportRow : productRow;
      const erpagRows = [exportHeaders, row, [], [], [], []];
      const ws = XLSX.utils.aoa_to_sheet(erpagRows);
      ws["!cols"] = exportHeaders.map(() => ({ wch: 20 }));
      XLSX.utils.book_append_sheet(wbProduct, ws, "Product Data");
    } else {
      const ws = XLSX.utils.aoa_to_sheet([]);
      let r = 1;
      XLSX.utils.sheet_add_aoa(ws, [["1. BASIC INFO"]], { origin: `A${r}` });
      ws["!merges"] = [{ s: { r: r - 1, c: 0 }, e: { r: r - 1, c: 3 } }];
      r++;
      [["SKU:", product.sku || "Not generated"], ["Description:", product.descriptionEn || ""], ["UPC:", product.upc || "Not generated"], ["UOM:", product.uom || "kg"]].forEach(([l, v]) => { XLSX.utils.sheet_add_aoa(ws, [[l, v]], { origin: `A${r}` }); r++; });
      r += 3;
      XLSX.utils.sheet_add_aoa(ws, [["2. ITEM CATEGORY"]], { origin: `A${r}` });
      ws["!merges"] = [...(ws["!merges"] || []), { s: { r: r - 1, c: 0 }, e: { r: r - 1, c: 3 } }];
      r++;
      [["Tax Category:", resolvedTaxCategory], ["Item Category:", product.itemCategory || ""], ["Trademark:", product.trademark || ""]].forEach(([l, v]) => { XLSX.utils.sheet_add_aoa(ws, [[l, v]], { origin: `A${r}` }); r++; });
      r += 3;
      XLSX.utils.sheet_add_aoa(ws, [["3. SELLING PRICE"]], { origin: `A${r}` });
      ws["!merges"] = [...(ws["!merges"] || []), { s: { r: r - 1, c: 0 }, e: { r: r - 1, c: 3 } }];
      r++;
      [["Default Price:", product.defaultPrice || ""], ["Type:", product.Pricetype || ""]].forEach(([l, v]) => { XLSX.utils.sheet_add_aoa(ws, [[l, v]], { origin: `A${r}` }); r++; });
      r += 3;
      XLSX.utils.sheet_add_aoa(ws, [["4. FULFILLMENT"]], { origin: `A${r}` });
      ws["!merges"] = [...(ws["!merges"] || []), { s: { r: r - 1, c: 0 }, e: { r: r - 1, c: 3 } }];
      r++;
      [["Back Order:", product.backOrder || ""], ["Base Quantity:", product.baseQuantity || ""]].forEach(([l, v]) => { XLSX.utils.sheet_add_aoa(ws, [[l, v]], { origin: `A${r}` }); r++; });
      r += 3;
      XLSX.utils.sheet_add_aoa(ws, [["5. ADDITIONAL INFO"]], { origin: `A${r}` });
      ws["!merges"] = [...(ws["!merges"] || []), { s: { r: r - 1, c: 0 }, e: { r: r - 1, c: 3 } }];
      r++;
      [["Description (EN):", product.descriptionEn || ""], ["Description (FR):", product.descriptionFr || ""], ["Ingredients (EN):", product.ingredientsEn || ""], ["Ingrédients (FR):", product.ingredientsFr || ""], ["Allergens (EN):", product.allergensEn || ""], ["Allergènes (FR):", product.allergensFr || ""]].forEach(([l, v]) => { XLSX.utils.sheet_add_aoa(ws, [[l, v]], { origin: `A${r}` }); r++; });
      ws["!cols"] = [{ wch: 28 }, { wch: 65 }, { wch: 18 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wbProduct, ws, "Product Data");
    }
    XLSX.writeFile(wbProduct, `${baseFileName}_ProductData.xlsx`);

    if (!isIngredientType) {
      const wbBom = XLSX.utils.book_new();
      const bomRows = buildBomSheetRows();
      const bomWs = XLSX.utils.aoa_to_sheet(bomRows);
      bomWs["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wbBom, bomWs, "BOM");
      XLSX.writeFile(wbBom, `${baseFileName}_BOM.xlsx`);
    }

    const wbSecondUom = XLSX.utils.book_new();
    const secondUomRows = buildSecondUomSheetRows();
    const secondUomWs = XLSX.utils.aoa_to_sheet(secondUomRows);
    const uomCount = Math.max(2, (secondUomRows[0].length - 1) / 6);
    secondUomWs["!cols"] = getSecondUomColWidths(uomCount);
    XLSX.utils.book_append_sheet(wbSecondUom, secondUomWs, "Second UOM");
    XLSX.writeFile(wbSecondUom, `${baseFileName}_SecondUOM.xlsx`);
    return;
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet([]);

  let row = 1;

  // ── 1. BASIC INFO ─────────────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["1. BASIC INFO"]], { origin: `A${row}` });
  ws["!merges"] = [{ s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } }];
  row++;

  [
    ["SKU:", product.sku || "Not generated"],
    ["Description:", product.descriptionEn || ""],
    ["UPC:", product.upc || "Not generated"],
    ["UOM:", product.uom || "kg"],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  XLSX.utils.sheet_add_aoa(ws, [["2. ITEM CATEGORY"]], { origin: `A${row}` });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["Tax Category:", resolvedTaxCategory],
    ["Item Category:", product.itemCategory || ""],
    ["Trademark:", product.trademark || ""],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  // ── 3. SELLING PRICE ──────────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["3. SELLING PRICE"]], { origin: `A${row}` });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["Default Price:", product.defaultPrice || ""],
    ["Type:", product.Pricetype || ""],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  // ── 4. FULFILLMENT ────────────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["4. FULFILLMENT"]], { origin: `A${row}` });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["Back Order:", product.backOrder || ""],
    ["Base Quantity:", product.baseQuantity || ""],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  // ── 5. BILL OF MATERIALS ──────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["5. BILL OF MATERIALS"]], {
    origin: `A${row}`,
  });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 6 } },
  ];
  row += 2;

  // Table Header
  XLSX.utils.sheet_add_aoa(
    ws,
    [
      [
        "SEQUENCE",
        "DESCRIPTION",
        "SKU / KEY",
        "QUANTITY",
        "UOM",
        "ESTIMATED COST",
        "ESTIMATED AMOUNT",
      ],
    ],
    { origin: `A${row}` }
  );
  row++;

  let sequenceCounter = 1;

  const filledIngredients = (bomIngredients || []).filter(isFilledIngredient);

  filledIngredients.forEach((ing) => {
    alert("Single Export");

    const seq = String(sequenceCounter).padStart(2, "0");
    const quantity = getAdjustedWeight(ing);
    const unitCost = Number(ing.unitCost || 0);
    const lineTotal = Number(ing.totalCost || 0).toFixed(3);

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        [
          seq,
          ing.name?.trim() || "(No name)",
          ing.sku || "",
          quantity > 0 ? quantity.toFixed(3) : "",
          ing.unit || "kg",
          unitCost > 0 ? unitCost.toFixed(4) : "",
          lineTotal > 0 ? lineTotal : "",
        ],
      ],
      { origin: `A${row}` }
    );
    row++;
    sequenceCounter++;
  });

  // Packaging items (continuing the same table)
  if (addedPackaging?.items?.length > 0) {
    row += 1;

    addedPackaging.items.forEach((item) => {
      const seq = String(sequenceCounter).padStart(2, "0");
      const qty = Number(item.qty || 1);
      const unitCost = Number(item.unitCost || 0);
      const lineTotal = (unitCost * qty).toFixed(3);

      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            seq,
            item.name?.trim() || "(Packaging Item)",
            item.sku || "",
            qty,
            "un",
            unitCost > 0 ? unitCost.toFixed(4) : "",
            lineTotal,
          ],
        ],
        { origin: `A${row}` }
      );
      row++;
      sequenceCounter++;
    });
  }

  row += 4;

  // ── 6. BARCODE / SECOND UOM ───────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["6. BARCODE / SECOND UOM"]], {
    origin: `A${row}`,
  });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["UPC:", product.upc || ""],
    ["SCC14:", product.scc14 || ""],
    ["Net Weight (g/kg):", product.netWeightGkKg || ""],
    ["g/kg Unit:", product.gkKgUnit || "kg"],
    ["Net Weight (oz/lbs):", product.netWeightOzLbs || ""],
    ["oz/lbs Unit:", product.ozLbsUnit || "oz"],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  // ── 7. ADDITIONAL INFO ────────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["7. ADDITIONAL INFO"]], { origin: `A${row}` });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["Description (EN):", product.descriptionEn || ""],
    ["Description (FR):", product.descriptionFr || ""],
    ["Ingredients (EN):", product.ingredientsEn || ""],
    ["Ingrédients (FR):", product.ingredientsFr || ""],
    ["Allergens (EN):", product.allergensEn || ""],
    ["Allergènes (FR):", product.allergensFr || ""],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });
  row += 3;

  // ── 8. CUSTOM FIELDS ──────────────────────────────────────────────────────────
  XLSX.utils.sheet_add_aoa(ws, [["8. CUSTOM FIELDS"]], { origin: `A${row}` });
  ws["!merges"] = [
    ...(ws["!merges"] || []),
    { s: { r: row - 1, c: 0 }, e: { r: row - 1, c: 3 } },
  ];
  row++;

  [
    ["FDA Code:", product.fdaCode || ""],
    ["FR:", product.fr || ""],
    ["Ingredients (EN):", product.ingredientsEn || ""],
    ["Ingrédients (FR):", product.ingredientsFr || ""],
    ["Allergens (EN):", product.allergensEn || ""],
    ["Allergènes (FR):", product.allergensFr || ""],
    ["Name FR:", product.nameFrCustom || product.nameFr || ""],
    ["Name EN:", product.nameEnCustom || product.nameEn || ""],
    ["Raw Material SKU:", product.rawMaterialSku || ""],
    ["Qty/SKU:", product.qtySku || ""],
    ["Net Weight (g/kg):", product.netWeightGkKg || ""],
    ["gkg:", product.gkKgUnit || ""],
    ["Net Weight (oz/lbs):", product.netWeightOzLbs || ""],
    ["ozlbs:", product.ozLbsUnit || ""],
    ["Catégorie:", product.categorie || ""],
    ["MSRP CAD:", product.msrpCad || ""],
    ["MSRP USD:", product.msrpUsd || ""],
    ["L(mm):", product.lengthMm || ""],
    ["W(mm):", product.widthMm || ""],
    ["H(mm):", product.heightMm || ""],
    ['L("):', product.lengthInch || ""],
    ['W("):', product.widthInch || ""],
    ['H("):', product.heightInch || ""],
  ].forEach(([label, value]) => {
    XLSX.utils.sheet_add_aoa(ws, [[label, value]], { origin: `A${row}` });
    row++;
  });

  // ── Column Widths ─────────────────────────────────────────────────────────────
  ws["!cols"] = [
    { wch: 28 },
    { wch: 65 },
    { wch: 18 },
    { wch: 14 },
    { wch: 10 },
    { wch: 18 },
    { wch: 18 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, "Recipe Export");

  const summaryData = [
    ["COST SUMMARY"],
    ["Raw Ingredients Total ($)", ingredientCostTotal.toFixed(3)],
    ["Packaging Total ($)", packagingCostNum.toFixed(3)],
    ["Total Batch Cost ($)", totalBatchCost.toFixed(3)],
    ["Accounting Cost per kg ($/kg)", acctCostPerKg],
  ];

  const summaryWS = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWS["!cols"] = [{ wch: 40 }, { wch: 25 }];
  XLSX.utils.book_append_sheet(wb, summaryWS, "Cost Summary");

  const safeName = (product.nameEn || "Recipe").replace(/[^a-zA-Z0-9]/g, "_");
  const dateStr = new Date().toISOString().slice(0, 10);
  const baseFileName = `${safeName}_Export_${dateStr}`;

  XLSX.writeFile(wb, `${baseFileName}_ProductData.xlsx`);

  const wbBom = XLSX.utils.book_new();
  const bomRows = buildBomSheetRows();
  const bomWs = XLSX.utils.aoa_to_sheet(bomRows);
  bomWs["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wbBom, bomWs, "BOM");
  XLSX.writeFile(wbBom, `${baseFileName}_BOM.xlsx`);

  const wbSecondUom = XLSX.utils.book_new();
  const secondUomRows = buildSecondUomSheetRows();
  const secondUomWs = XLSX.utils.aoa_to_sheet(secondUomRows);
  const uomCount = Math.max(2, (secondUomRows[0].length - 1) / 6);
    secondUomWs["!cols"] = getSecondUomColWidths(uomCount);
  XLSX.utils.book_append_sheet(wbSecondUom, secondUomWs, "Second UOM");
  XLSX.writeFile(wbSecondUom, `${baseFileName}_SecondUOM.xlsx`);
};

export const exportRecipeBatchToExcel = (items = []) => {
  alert("Batch Export");
  const batchItems = Array.isArray(items) ? items : [];
  if (batchItems.length === 0) return;

  const ERPAG_HEADERS = [
    "SKU",
    "Description",
    "EAN/UPC",
    "UOM",
    "Selling price",
    "Category",
    "Trademark",
    "Tax category",
    "Supplier",
    "Base Quantity",
    "Purchase price",
    "Landed cost",
    "SKU (Supplier)",
    "FDA Code",
    "FR",
    "Ingredients",
    "Ingrédients",
    "Allergens",
    "Allergènes",
    "Name EN",
    "Name FR",
    "Raw Material SKU",
    "Qty/Un",
    "Un",
    "Net Weight 1",
    "Unit 1",
    "Net Weight 2",
    "Unit 2",
    "Catégorie",
    "MSRP (CAD)",
    "MSRP (USD)",
    "L ('')",
    "W ('')",
    "H ('')",
    "L (mm)",
    "W (mm)",
    "H (mm)",
    "Ti",
    "Hi",
    "New",
    "Label Category",
    "Country of Origin",
    "Harmonized Tariff",
    "Expiry Date",
  ];

  const resolveTaxCategory = (product = {}) =>
    product?.taxCategory ||
    product?.tax_category ||
    product?.taxcategory ||
    product?.customFields?.taxCategory ||
    "Tax exemption";

  const resolveExpiryDays = (value) => {
    const trimmed = String(value || "").trim();
    return trimmed ? trimmed : "1095";
  };

  const isBatchIngredientOnly = batchItems.every((entry) =>
    String(entry?.product?.type || "")
      .trim()
      .toLowerCase() === "ingredient",
  );
  let batchHeaders = isBatchIngredientOnly
    ? [...RAW_MATERIAL_IMPORT_HEADERS]
    : [...ERPAG_HEADERS];

  const allAdditionalKeys = new Set();
  batchItems.forEach(entry => {
    if (entry.additionalExportColumns) {
      Object.keys(entry.additionalExportColumns).forEach(k => allAdditionalKeys.add(k));
    }
  });
  const additionalColKeys = Array.from(allAdditionalKeys);
  if (additionalColKeys.length > 0) {
    batchHeaders = batchHeaders.concat(additionalColKeys);
  }
  const productRows = [batchHeaders];
  const bomRows = [["SKU", "SKU", "QUANTITY"]];
  let maxBatchUomCount = 2;
  batchItems.forEach((entry) => {
    const bRows = Array.isArray(entry?.barcodeSecondUomRows)
      ? entry.barcodeSecondUomRows.filter((row) => String(row?.eanUpc || "").trim())
      : [];
    if (bRows.length > maxBatchUomCount) {
      maxBatchUomCount = bRows.length;
    }
  });
  const secondUomRows = [getSecondUomHeaders(maxBatchUomCount)];

  batchItems.forEach((entry) => {
    const product = entry?.product || {};
    const sku = String(product?.sku || product?.baseProductSku || "").trim();
    if (!sku) return;

    const resolvedTaxCategory = resolveTaxCategory(product);
    const isIngredientType =
      String(product?.type || "")
        .trim()
        .toLowerCase() === "ingredient";
    const fulfillmentValue = isIngredientType
      ? product?.supplier || ""
      : "Nutrivilla Foods";
    const masterCaseWeightKgForRow =
      Number(entry?.masterCaseWeightKg) ||
      Number(product?.baseQuantity) ||
      Number(product?.masterWeightKg) ||
      0;

    const wholesale3Cad = Number(entry?.wholesale3Cad) || 0;

    const sellingPrice = numberFrom(product, ["defaultPrice", "sellingPrice"]);
    const purchasePrice = numberFrom(product, [
      "purchasePrice",
      "suppliers.purchasePrice",
    ]);
    const landingCost = numberFrom(product, [
      "landingCost",
      "suppliers.landingCost",
    ]);
    const estimatedCost = isIngredientType
      ? purchasePrice
      : Number(entry?.acctCostPerKg || 0);
    const resolvedMsrpCad = isIngredientType
      ? product?.msrpCad === "" || product?.msrpCad === null || product?.msrpCad === undefined
        ? 0
        : product.msrpCad
      : product?.msrpCad || "";
    const resolvedMsrpUsd = isIngredientType
      ? product?.msrpUsd === "" || product?.msrpUsd === null || product?.msrpUsd === undefined
        ? 0
        : product.msrpUsd
      : product?.msrpUsd || "";
    const productRow = [
      sku,
      product.descriptionEn || "",
      product.upc || "",
      product.uom || "kg",
      wholesale3Cad > 0 && masterCaseWeightKgForRow > 0
        ? Number(wholesale3Cad / masterCaseWeightKgForRow).toFixed(2)
        : (product.defaultPrice || ""),
      product.itemCategory || "",
      product.trademark || "",
      resolvedTaxCategory,
      fulfillmentValue,
      masterCaseWeightKgForRow || "",
      // estimatedCost ? estimatedCost.toFixed(4) : "",

      // landingCost > 0 ? landingCost.toFixed(4) : "",
      "", // Purchase price hidden for base blends
      landingCost > 0 ? landingCost.toFixed(4) : "",
      isIngredientType
        ? product.supplierSku || ""
        : product.supplier || "",
      product.fdaCode || "",
      buildFrDescription(product),
      product.ingredientsEn || "",
      product.ingredientsFr || "",
      product.allergensEn || "",
      product.allergensFr || "",
      product.nameEnCustom || product.nameEn || "",
      product.nameFrCustom || product.nameFr || "",
      product.rawMaterialSku || sku,
      product.qtySku || "",
      product.qtySkuUnit || product.uom || "",
      product.netWeightGkKg || "",
      product.gkKgUnit || "",
      product.netWeightOzLbs || "",
      resolveUnit2(product.uom),
      product.categorie || "",
      toPerMasterCaseValue(resolvedMsrpCad, masterCaseWeightKgForRow),
      toPerMasterCaseValue(resolvedMsrpUsd, masterCaseWeightKgForRow),
      product.lengthInch || "",
      product.widthInch || "",
      product.heightInch || "",
      product.lengthMm || "",
      product.widthMm || "",
      product.heightMm || "",
      entry?.addedPackaging?.ti || product.ti || "",
      entry?.addedPackaging?.hi || product.hi || "",
      product.new || "New",
      "",
      product.countryOfOrigin || "",
      product.harmonizedTariff || "",
      resolveExpiryDays(product.expiryDays),
      ...additionalColKeys.map(k => entry?.additionalExportColumns?.[k] || "")
    ];
    const ingredientRow = [
      sku,
      product.descriptionEn || product.description || "",
      product.upc || product.eanUpc || "",
      product.uom || "kg",
      wholesale3Cad > 0
        ? Number(wholesale3Cad).toFixed(2)
        : sellingPrice > 0
        ? sellingPrice
        : "",
      product.itemCategory || product.category || "",
      product.trademark || "",
      resolvedTaxCategory,
      valueFrom(product, ["supplier", "suppliers.supplier"]),
      "",
      purchasePrice > 0 ? purchasePrice.toFixed(4) : "",
      landingCost > 0 ? landingCost.toFixed(4) : "",
      product.supplierSku || "",
      product.fdaCode || valueFrom(product, ["customFields.fdaCode"]),
      product.fr || product.descriptionFr || valueFrom(product, ["customFields.fr"]),
      product.ingredientsEn || valueFrom(product, ["customFields.ingredients"]),
      product.ingredientsFr || product.customFields?.["ingrÃ©dients"] || "",
      product.allergensEn || valueFrom(product, ["customFields.allergens"]),
      product.allergensFr || product.customFields?.["allergÃ¨nes"] || "",
      product.nameEnCustom || product.nameEn || valueFrom(product, ["customFields.nameEn"]),
      product.nameFrCustom || product.nameFr || valueFrom(product, ["customFields.nameFr"]),
      product.rawMaterialSku || valueFrom(product, ["customFields.rawMaterialSku"]) || sku,
      product.qtySku || valueFrom(product, ["customFields.qtyUn"]),
      product.qtySkuUnit || valueFrom(product, ["customFields.un"]) || product.uom || "",
      product.netWeightGkKg || valueFrom(product, ["customFields.netWeightGKg"]),
      product.gkKgUnit || valueFrom(product, ["customFields.gKg"]),
      product.netWeightOzLbs || valueFrom(product, ["customFields.netWeightOzLbs"]),
      resolveUnit2(product.uom),
      product.categorie || product.customFields?.["catÃ©gorie"] || "",
      resolvedMsrpCad,
      resolvedMsrpUsd,
      product.lengthInch || "",
      product.widthInch || "",
      product.heightInch || "",
      product.lengthMm || "",
      product.widthMm || "",
      product.heightMm || "",
      entry?.addedPackaging?.ti || product.ti || "",
      entry?.addedPackaging?.hi || product.hi || "",
      product.new || valueFrom(product, ["customFields.new"]) || "New",
      product.labelCategory || valueFrom(product, ["customFields.labelCategory"]),
      product.countryOfOrigin || "",
      product.harmonizedTariff || "",
      valueFrom(product, ["shippingDays", "suppliers.shippingDays"]),
      valueFrom(product, [
        "minimumOrderQuantity",
        "suppliers.minimumOrderQuantity",
      ]),
      valueFrom(product, ["orderStepQuantity", "suppliers.orderStepQuantity"]),
      resolveExpiryDays(product.expiryDays || valueFrom(product, ["customFields.expiryDays"])),
      ...additionalColKeys.map(k => entry?.additionalExportColumns?.[k] || "")
    ];
    const rawMaterialImportRow = [
      sku,
      product.descriptionEn || product.description || "",
      product.upc || product.eanUpc || "",
      product.uom || "kg",
      wholesale3Cad > 0 && masterCaseWeightKgForRow > 0
        ? Number(wholesale3Cad / masterCaseWeightKgForRow).toFixed(2)
        : sellingPrice > 0
        ? sellingPrice
        : "",
      product.itemCategory || product.category || "",
      product.trademark || "",
      "",
      resolvedTaxCategory,
      "",
      "",
      valueFrom(product, ["supplier", "suppliers.supplier"]),
      purchasePrice > 0 ? purchasePrice.toFixed(4) : "",
      landingCost > 0 ? landingCost.toFixed(4) : "",
      product.supplierSku || "",
      product.harmonizedTariff || "",
      "",
      valueFrom(product, [
        "minimumOrderQuantity",
        "suppliers.minimumOrderQuantity",
      ]),
      valueFrom(product, ["orderStepQuantity", "suppliers.orderStepQuantity"]),
      product.fdaCode || valueFrom(product, ["customFields.fdaCode"]),
      product.fr || product.descriptionFr || valueFrom(product, ["customFields.fr"]),
      product.ingredientsEn || valueFrom(product, ["customFields.ingredients"]),
      product.ingredientsFr || product.customFields?.["ingrÃ©dients"] || "",
      product.allergensEn || valueFrom(product, ["customFields.allergens"]),
      product.allergensFr || product.customFields?.["allergÃ¨nes"] || "",
      product.nameEnCustom || product.nameEn || valueFrom(product, ["customFields.nameEn"]),
      product.nameFrCustom || product.nameFr || valueFrom(product, ["customFields.nameFr"]),
      product.rawMaterialSku || valueFrom(product, ["customFields.rawMaterialSku"]) || sku,
      product.qtySku || valueFrom(product, ["customFields.qtyUn"]),
      product.qtySkuUnit || valueFrom(product, ["customFields.un"]) || product.uom || "",
      product.netWeightGkKg || valueFrom(product, ["customFields.netWeightGKg"]),
      product.gkKgUnit || valueFrom(product, ["customFields.gKg"]),
      product.netWeightOzLbs || valueFrom(product, ["customFields.netWeightOzLbs"]),
      resolveUnit2(product.uom),
      product.categorie || product.customFields?.["catÃ©gorie"] || "",
      resolvedMsrpCad,
      resolvedMsrpUsd,
      product.lengthInch || "",
      product.widthInch || "",
      product.heightInch || "",
      product.lengthMm || "",
      product.widthMm || "",
      product.heightMm || "",
      entry?.addedPackaging?.ti || product.ti || "",
      entry?.addedPackaging?.hi || product.hi || "",
      product.new || valueFrom(product, ["customFields.new"]) || "New",
      product.labelCategory || valueFrom(product, ["customFields.labelCategory"]),
      product.countryOfOrigin || "",
      resolveExpiryDays(product.expiryDays || valueFrom(product, ["customFields.expiryDays"])),
      ...additionalColKeys.map(k => entry?.additionalExportColumns?.[k] || "")
    ];
    productRows.push(isBatchIngredientOnly && isIngredientType ? rawMaterialImportRow : (isIngredientType ? ingredientRow : productRow));

    const ingredients = Array.isArray(entry?.ingredients) ? entry.ingredients : [];
    const masterCaseWeightKg = Number(entry?.masterCaseWeightKg) || 0;
    ingredients
      .filter((ing) => String(ing?.sku || "").trim())
      .forEach((ing) => {
        const ratio = Number(ing?.ratio) || 0;
        const ratioAdjustedQty =
          ratio > 0 && masterCaseWeightKg > 0
            ? (ratio / 100) * masterCaseWeightKg
            : Number(ing?.weight) || 0;
        bomRows.push([sku, String(ing.sku || "").trim(), ratioAdjustedQty]);
      });

    const packagingItems = entry?.addedPackaging?.items || [];
    packagingItems
      .filter((item) => String(item?.sku || "").trim())
      .forEach((item) => {
        bomRows.push([
          sku,
          String(item.sku || "").trim(),
          Number(item.qty) || 0,
        ]);
      });

    const barcodeRows = Array.isArray(entry?.barcodeSecondUomRows)
      ? entry.barcodeSecondUomRows.filter((row) => String(row?.eanUpc || "").trim())
      : [];
    const sortedBarcodeRows = [...barcodeRows].sort((a, b) => {
      const qA = Number(a?.quantityDefault) || 0;
      const qB = Number(b?.quantityDefault) || 0;
      return qA - qB;
    });

    const uomRowData = [sku];
    for (let i = 0; i < maxBatchUomCount; i++) {
      const uom = sortedBarcodeRows[i];
      if (uom) {
        uomRowData.push(
          String(uom.eanUpc || "").trim(),
          String(uom.secondUom || "").trim(),
          Number(uom.quantityDefault) || 0,
          String(uom.description || "").trim(),
          String(uom.type || "").trim(),
          String(uom.labelPrinting || "").trim()
        );
      } else {
        uomRowData.push("", "", "", "", "", "");
      }
    }
    secondUomRows.push(uomRowData);
  });

  const dateStr = new Date().toISOString().slice(0, 10);
  const baseFileName = `Batch_Export_${dateStr}`;

  const wbProduct = XLSX.utils.book_new();
  const wsProduct = XLSX.utils.aoa_to_sheet(productRows);
  wsProduct["!cols"] = batchHeaders.map(() => ({ wch: 20 }));
  XLSX.utils.book_append_sheet(wbProduct, wsProduct, "Product Data");
  XLSX.writeFile(wbProduct, `${baseFileName}_ProductData.xlsx`);

  if (isBatchIngredientOnly) return;

  const wbBom = XLSX.utils.book_new();
  const wsBom = XLSX.utils.aoa_to_sheet(bomRows);
  wsBom["!cols"] = [{ wch: 18 }, { wch: 18 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wbBom, wsBom, "BOM");
  XLSX.writeFile(wbBom, `${baseFileName}_BOM.xlsx`);

  const wbSecondUom = XLSX.utils.book_new();
  const wsSecondUom = XLSX.utils.aoa_to_sheet(secondUomRows);
  wsSecondUom["!cols"] = getSecondUomColWidths(maxBatchUomCount);
  XLSX.utils.book_append_sheet(wbSecondUom, wsSecondUom, "Second UOM");
  XLSX.writeFile(wbSecondUom, `${baseFileName}_SecondUOM.xlsx`);
};




